import React, { useEffect, useRef, useState } from "react";
import { useConnector } from "../game-engine/connector/context";
import { useWindowSize } from "usehooks-ts";
import { useBattleContext } from "../game-engine/context";
import { useSelector } from "react-redux";
import useArenaLiveChecking from "../game-engine/hooks/useArenaLiveChecking";
import { getProfileURL } from "../utils/profile-url";
import eventBus, { EventNames } from "../game-engine/utils/event-bus";
import { Badge } from "react-bootstrap";
import Countdown from "react-countdown";
import { CustomIcon } from "./Icons";
import classNames from "classnames";
import { ConnectionStatus } from "../game-engine/types/enum";
import CircleProgressBar from "../game-engine/components/circle-progress-bar";
import CircularProgress from "./CircularProgress";
import Lottie from "lottie-react";
import searchAnimation from "../animations/search.json";
import swordAnimation from "../animations/swords.json";
import { isMobile } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import useTriggerEvent from "../game-engine/hooks/useTriggerEvent";
import { useAppTranslation } from "../i18n/useAppTranslation";
type Props = {
  animationSlide?: boolean;
  isHovering?: boolean;
};

const PlayButton = (props: Props) => {
  const { t } = useAppTranslation(["navigation", "battle"]);
  const [isClicked, setIsClicked] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const { connector } = useConnector();
  const { width } = useWindowSize();
  const { connectionStatus } = useBattleContext();
  const [ping, setPing] = useState(0);
  const userInfo = useSelector((state: any) => state.userInfo);
  const { isFindMatchOpen } = useArenaLiveChecking();
  const findMatchTimeout = useRef<any>();
  const { hash, pathname } = useLocation();
  const { isFindingMatch, setIsFindingMatch, gameInfo } = useBattleContext();
  const triggerEventBus = useTriggerEvent();
  const gameInfoRef = useRef(gameInfo);
  const onFindMatch = () => {
    if (!isFindMatchOpen || connectionStatus !== ConnectionStatus.Connected) {
      return;
    }
    if (isFindingMatch) {
      cancelSearch();
      return;
    }
    connector?.findMatch(
      userInfo.username,
      getProfileURL(userInfo.id, userInfo.photoFile) || ""
    );
    setIsFindingMatch(true);
    startTimer();
  };
  useEffect(() => {
    if (hash && hash === "#find-match") {
      triggerEventBus(EventNames.ClosePopup, null);
      window.history.replaceState("", document.title, window.location.pathname);
      onFindMatch();
    }
  }, [hash]);

  useEffect(() => {
    if (gameInfo) {
      gameInfoRef.current = gameInfo;
    }
  }, [gameInfo]);

  useEffect(() => {
    eventBus.on(EventNames.Ping, (pingValue: number) => {
      setPing(pingValue);
    });
    return () => {
      eventBus.off(EventNames.Ping, (pingValue: number) => {
        setPing(pingValue);
      });
    };
  }, []);

  const startTimer = () => {
    if (!gameInfoRef.current?.gameRules.findMatchTimeout) return;
    findMatchTimeout.current = setTimeout(() => {
      setIsFindingMatch(false);
      setIsClicked(false);
      findMatchTimeout.current = null;
    }, gameInfoRef.current.gameRules.findMatchTimeout);
  };

  const cancelSearch = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e?.preventDefault();
    e?.stopPropagation();
    connector?.notReady();
    setIsFindingMatch(false);
    clearTimeout(findMatchTimeout.current);
    findMatchTimeout.current = null;
  };

  return (
    <div
      style={{ marginBottom: !isMobile ? "-29px" : "-20px" }}
      className="mx-auto w-fit"
    >
      <a
        onClick={() => onFindMatch()}
        className={classNames("button play-btn position-relative", {
          "play-disabled":
            !isFindMatchOpen && connectionStatus === ConnectionStatus.Connected,
          "button-disabled":
            connectionStatus === ConnectionStatus.Disconnected ||
            connectionStatus === ConnectionStatus.Connecting,
        })}
      >
        {isFindMatchOpen && (
          <Badge
            className="bg-danger"
            style={{
              position: "absolute",
              top: 0,
              right: 20,
              width: "fit-content",
            }}
            bg="danger"
          >
            {t("battle:findMatch.live")}
          </Badge>
        )}

        {isFindingMatch ? (
          <Lottie style={{ width: 80 }} animationData={searchAnimation} loop />
        ) : isFindMatchOpen ? (
          <Lottie
            style={{ width: "100%", marginTop: isMobile ? "-18px" : "-30px" }}
            animationData={swordAnimation}
            autoplay={isFindMatchOpen}
            loop={isFindMatchOpen}
          />
        ) : (
          <img
            src={"/assets/images/icons/play-blades.svg"}
            width={isMobile ? 50 : 73}
          />
        )}
      </a>
      <div className=" text-center play-text">
        {connectionStatus === ConnectionStatus.Connected ? (
          <>
            {isFindMatchOpen && !isFindingMatch && t("navigation:play")}
            {isFindingMatch && t("battle:findMatch.finding")}
            {!isFindMatchOpen && (
              <Countdown
                date={new Date(gameInfo?.openTime || "")}
                renderer={({ days, hours, minutes, seconds }) => (
                  <motion.div
                      animate={{
                        y: props?.isHovering ? 0 :  props.animationSlide ? -20 : 0,
                        width: props.animationSlide&& !props?.isHovering?"fit-content": undefined,
                      }}
                    style={{
                      fontSize: props.animationSlide && !props?.isHovering?10: width < 768 ? 12 : 20,
                    }}
                    className={classNames("dark-yellow d-flex align-items-center gap-1 justify-content-center",{
                      "bg-blur rounded p-1 mx-auto  fs-tiny":props.animationSlide && !props?.isHovering 
                    })}
                  >
                    <CustomIcon
                      icon="sand-timer"
                      width={props.animationSlide && !props?.isHovering?10: isMobile ? 10 : 20}
                      height={props.animationSlide && !props?.isHovering?10: isMobile ? 10 : 20}
                      type="svg"
                    />
                    {hours}h {minutes}m {seconds}s
                  </motion.div>
                )}
              />
            )}
          </>
        ) : (
          connectionStatus
        )}
      </div>
    </div>
  );
};

export default PlayButton;
